/**
 * PATH: apps/quote/src/pages/claims/ExternalPriorPage.tsx
 * FILE_ID: QUOTE-CLAIMS-G10-EXTERNAL-001
 * PURPOSE: Register an immutable externally-issued prior claim.
 * WHY: Carry-forward is unsafe when pre-Quotech history is omitted.
 */
import { useEffect, useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { ArrowLeft, FileUp, Loader2 } from 'lucide-react'
import { Link, useNavigate } from 'react-router-dom'
import {
  listClaimContracts,
  registerExternalPrior,
} from '@quotech/api/g10ProgressClaims'
import { useApi } from '@/lib/setupApi'

const FIELD = 'mt-1 min-h-11 w-full rounded-lg border px-3 py-2 text-sm dark:bg-slate-900'

export function ExternalPriorPage({ jobId }: { jobId: number }) {
  const api = useApi()
  const navigate = useNavigate()
  const contracts = useQuery({
    queryKey: ['claim-contracts', jobId],
    queryFn: () => listClaimContracts(api, jobId),
  })
  const [contractId, setContractId] = useState('')
  const [period, setPeriod] = useState('')
  const [externalRef, setExternalRef] = useState('')
  const [percent, setPercent] = useState('')
  const [file, setFile] = useState<File | null>(null)
  useEffect(() => {
    if (!contractId && contracts.data?.[0]) setContractId(contracts.data[0].id)
  }, [contractId, contracts.data])
  const register = useMutation({
    mutationFn: () => registerExternalPrior(api, {
      jobId,
      contractId,
      period,
      externalRef,
      percentComplete: Number(percent),
      file: file as File,
    }),
    onSuccess: (claim) => navigate(`/claims/${claim.id}`, { replace: true }),
  })
  const valid = Boolean(
    contractId && period && externalRef && file
    && percent !== '' && Number(percent) >= 0 && Number(percent) <= 100
  )
  const detail = (register.error as { response?: { data?: { detail?: string } } } | null)
    ?.response?.data?.detail

  return (
    <div className="min-h-screen bg-slate-50 p-4 sm:p-6 dark:bg-slate-900">
      <main className="mx-auto max-w-2xl space-y-5 rounded-xl border bg-white p-5 dark:bg-slate-800">
        <header className="flex items-center gap-3">
          <Link to="/claims" aria-label="Back to claims" className="rounded-lg border p-2"><ArrowLeft className="h-5 w-5" /></Link>
          <div><h1 className="text-xl font-bold">Upload prior claim</h1><p className="text-sm text-slate-500">Register a claim already issued outside Quotech so the next period cannot double-claim it.</p></div>
        </header>
        {detail && <p role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">{detail}</p>}
        <label className="block text-sm font-medium">Contract or work package<select value={contractId} onChange={(event) => setContractId(event.target.value)} className={FIELD}><option value="">Select contract</option>{contracts.data?.map((contract) => <option key={contract.id} value={contract.id}>{contract.title}{contract.work_package ? ` · ${contract.work_package}` : ''}</option>)}</select></label>
        {contracts.isError && <p role="alert" className="text-sm text-red-600">Claim contracts could not be loaded.</p>}
        <div className="grid gap-4 sm:grid-cols-2">
          <label className="text-sm font-medium">Period<input value={period} onChange={(event) => setPeriod(event.target.value)} placeholder="May 2026" className={FIELD} /></label>
          <label className="text-sm font-medium">External reference<input value={externalRef} onChange={(event) => setExternalRef(event.target.value)} placeholder="PC05" className={FIELD} /></label>
          <label className="text-sm font-medium">Cumulative completion %<input type="number" min={0} max={100} value={percent} onChange={(event) => setPercent(event.target.value)} className={FIELD} /></label>
          <label className="text-sm font-medium">Issued document<input type="file" accept=".pdf,.doc,.docx,.xls,.xlsx" onChange={(event) => setFile(event.target.files?.[0] ?? null)} className={`${FIELD} file:mr-2`} /></label>
        </div>
        <p className="text-xs text-slate-500">Use the cumulative percentage only when line-level history is unavailable. The uploaded source is checksummed and retained as provenance.</p>
        <div className="flex justify-end"><button type="button" disabled={!valid || register.isPending} onClick={() => register.mutate()} className="inline-flex min-h-11 items-center gap-2 rounded-lg bg-amber-500 px-4 font-medium text-white disabled:opacity-50">{register.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileUp className="h-4 w-4" />}Register prior claim</button></div>
      </main>
    </div>
  )
}
